/**
 * Pixel-accurate, lightweight React replicas of real RotaHQ screens.
 *
 * These are the landing page's "screenshots": pure DOM + the app's own
 * design tokens, so they render razor-sharp at any DPI, cost zero image
 * bytes, and always match the actual product styling. Illustrative data
 * only — realistic UK restaurant names, roles and figures.
 */
import { Fragment } from 'react';
import { CalendarDays, CheckCircle2, Clock, Delete, FileText, Flame, ShieldCheck, Upload, Users } from 'lucide-react';

const STAFF = [
  { name: 'Sofia Rossi', initials: 'SR', role: 'Head Chef', rate: '£16.50/h' },
  { name: 'James Carter', initials: 'JC', role: 'Sous Chef', rate: '£14.00/h' },
  { name: 'Amelia Khan', initials: 'AK', role: 'Server', rate: '£11.60/h' },
  { name: 'Tomás Silva', initials: 'TS', role: 'Bartender', rate: '£12.20/h' },
  { name: 'Grace Obi', initials: 'GO', role: 'Server', rate: '£11.60/h' },
];

/** App-window chrome shared by every replica. */
export function ReplicaFrame({ label, children, className = '' }) {
  return (
    <div className={`surface-card overflow-hidden select-none ${className}`} role="img" aria-label={`Preview of the RotaHQ ${label} screen`}>
      <div className="flex items-center justify-between px-3.5 py-2 border-b border-[color:var(--brand-border)] bg-[color:var(--brand-surface-2)]">
        <div className="flex items-center gap-2 min-w-0">
          <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-[color:var(--brand-primary)] text-white">
            <Clock className="h-3 w-3" />
          </div>
          <span className="font-display text-xs font-semibold truncate">RotaHQ</span>
          <span className="text-[10px] text-[color:var(--brand-text-muted)] truncate">· The Copper Pot</span>
        </div>
        <span className="text-[10px] font-mono uppercase tracking-wide text-[color:var(--brand-text-muted)]">{label}</span>
      </div>
      <div className="p-3 sm:p-3.5">{children}</div>
    </div>
  );
}

const DAYS = ['Mon 2', 'Tue 3', 'Wed 4', 'Thu 5', 'Fri 6', 'Sat 7', 'Sun 8'];
const ROTA = [
  ['10–16', null, '10–16', '10–16', '12–22', '12–22', null],
  [null, '12–20', '12–20', null, '16–23', '16–23', '11–17'],
  ['11–15', '11–15', null, '17–22', '17–22', '17–23', null],
  [null, '17–23', '17–23', '17–23', null, '18–00', '12–18'],
  ['17–22', null, null, '11–15', '11–15', '11–17', '11–17'],
];

export function RotaReplica({ className = '' }) {
  return (
    <ReplicaFrame label="Schedule" className={className}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5 text-xs font-semibold">
          <CalendarDays className="h-3.5 w-3.5 text-[color:var(--brand-primary)]" />
          Week of 2 March
        </div>
        <span className="pill bg-[color:var(--brand-success-soft)] text-[color:var(--brand-success)] !text-[10px]">Published</span>
      </div>
      <div className="grid gap-px bg-[color:var(--brand-border)] rounded-lg overflow-hidden border border-[color:var(--brand-border)] text-[9px] sm:text-[10px]" style={{ gridTemplateColumns: '5.5rem repeat(7, minmax(0,1fr))' }}>
        <div className="bg-[color:var(--brand-bg-2)] px-1.5 py-1 font-semibold">Staff</div>
        {DAYS.map((d) => (
          <div key={d} className="bg-[color:var(--brand-bg-2)] px-1 py-1 text-center font-semibold text-[color:var(--brand-text-muted)]">{d}</div>
        ))}
        {STAFF.map((emp, i) => (
          <Fragment key={emp.name}>
            <div className="bg-white px-1.5 py-1.5 truncate">
              <div className="font-semibold truncate">{emp.name}</div>
              <div className="text-[color:var(--brand-text-muted)] truncate">{emp.role}</div>
            </div>
            {ROTA[i].map((shift, j) => (
              <div key={`${emp.name}-${j}`} className="bg-white px-0.5 py-1.5 flex items-start justify-center">
                {shift ? (
                  <span className="rounded bg-[color:var(--brand-primary-soft)] text-[color:var(--brand-primary)] font-semibold px-1 py-0.5 whitespace-nowrap">{shift}</span>
                ) : (
                  <span className="text-[color:var(--brand-border)]">—</span>
                )}
              </div>
            ))}
          </Fragment>
        ))}
      </div>
      <div className="flex items-center justify-between mt-2 text-[10px] text-[color:var(--brand-text-muted)]">
        <span>5 staff · 23 shifts · 148.5h scheduled</span>
        <span className="text-[color:var(--brand-primary)] font-semibold">Publish week →</span>
      </div>
    </ReplicaFrame>
  );
}

export function KioskReplica({ className = '' }) {
  return (
    <ReplicaFrame label="Kiosk" className={className}>
      <div className="kiosk-bg rounded-lg border border-[color:var(--brand-border)] px-4 py-5 text-center">
        <div className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--brand-text-muted)]">Friday · 17:58</div>
        <div className="font-display text-sm font-semibold mt-0.5">Enter your PIN to clock in</div>
        <div className="flex justify-center gap-2 my-3" aria-hidden="true">
          {[1, 2, 3, 4].map((i) => (
            <span key={i} className={`h-2.5 w-2.5 rounded-full ${i <= 2 ? 'bg-[color:var(--brand-primary)]' : 'border border-[color:var(--brand-border)] bg-white'}`} />
          ))}
        </div>
        <div className="grid grid-cols-3 gap-1.5 max-w-[180px] mx-auto">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
            <span key={n} className="rounded-lg bg-white border border-[color:var(--brand-border)] py-1.5 text-xs font-semibold shadow-sm">{n}</span>
          ))}
          <span />
          <span className="rounded-lg bg-white border border-[color:var(--brand-border)] py-1.5 text-xs font-semibold shadow-sm">0</span>
          <span className="rounded-lg bg-white border border-[color:var(--brand-border)] py-1.5 flex items-center justify-center shadow-sm"><Delete className="h-3.5 w-3.5 text-[color:var(--brand-text-muted)]" /></span>
        </div>
        <div className="grid grid-cols-2 gap-1.5 max-w-[180px] mx-auto mt-2">
          <span className="rounded-lg bg-[color:var(--brand-success)] text-white py-1.5 text-[11px] font-semibold">Clock in</span>
          <span className="rounded-lg bg-white border border-[color:var(--brand-border)] py-1.5 text-[11px] font-semibold">Clock out</span>
        </div>
      </div>
    </ReplicaFrame>
  );
}

const ATTENDANCE = [
  { name: 'Sofia Rossi', shift: '10:00–16:00', actual: '09:56 → 16:04', hours: '6.1h', status: 'On time', tone: 'success' },
  { name: 'James Carter', shift: '12:00–20:00', actual: '12:14 → 20:02', hours: '7.8h', status: 'Late 14m', tone: 'warning' },
  { name: 'Amelia Khan', shift: '17:00–22:00', actual: '16:55 → —', hours: 'open', status: 'Clocked in', tone: 'info' },
  { name: 'Tomás Silva', shift: '17:00–23:00', actual: '— → —', hours: '—', status: 'No show', tone: 'danger' },
];

const TONE_CLS = {
  success: 'bg-[color:var(--brand-success-soft)] text-[color:var(--brand-success)]',
  warning: 'bg-[color:var(--brand-warning-soft)] text-[color:var(--brand-warning)]',
  info: 'bg-[color:var(--brand-info-soft)] text-[color:var(--brand-info)]',
  danger: 'bg-[color:var(--brand-danger-soft)] text-[color:var(--brand-danger)]',
};

export function AttendanceReplica({ className = '' }) {
  return (
    <ReplicaFrame label="Attendance" className={className}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-semibold">Schedule check · Friday 6 March</div>
        <span className="pill bg-[color:var(--brand-bg-2)] text-[color:var(--brand-text-muted)] !text-[10px]">Scheduled vs actual</span>
      </div>
      <div className="rounded-lg border border-[color:var(--brand-border)] overflow-hidden divide-y divide-[color:var(--brand-border)] text-[10px] sm:text-[11px]">
        <div className="grid grid-cols-[1.3fr_1fr_1.2fr_0.9fr] bg-[color:var(--brand-bg-2)] px-2 py-1.5 font-semibold text-[color:var(--brand-text-muted)]">
          <span>Staff</span><span>Scheduled</span><span>Clock in → out</span><span className="text-right">Status</span>
        </div>
        {ATTENDANCE.map((row) => (
          <div key={row.name} className="grid grid-cols-[1.3fr_1fr_1.2fr_0.9fr] items-center bg-white px-2 py-1.5">
            <span className="font-semibold truncate">{row.name}</span>
            <span className="font-mono">{row.shift}</span>
            <span className="font-mono text-[color:var(--brand-text-muted)]">{row.actual}</span>
            <span className="text-right"><span className={`pill !text-[9px] ${TONE_CLS[row.tone]}`}>{row.status}</span></span>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-1.5 mt-2 text-[10px] text-[color:var(--brand-text-muted)]">
        <ShieldCheck className="h-3 w-3 text-[color:var(--brand-success)]" />
        Missed clock-outs flagged automatically — nothing changes without your say-so.
      </div>
    </ReplicaFrame>
  );
}

const PAYROLL = [
  { name: 'Sofia Rossi', hours: '152.5h', overtime: '+4.5h', gross: '£2,283.75', net: '£1,896.42' },
  { name: 'James Carter', hours: '148.0h', overtime: '—', gross: '£2,072.00', net: '£1,748.11' },
  { name: 'Amelia Khan', hours: '96.0h', overtime: '—', gross: '£1,113.60', net: '£1,046.90' },
];

export function PayrollReplica({ className = '' }) {
  return (
    <ReplicaFrame label="Payroll" className={className}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-semibold">Payroll draft · 1–31 March</div>
        <span className="pill bg-[color:var(--brand-accent-soft)] text-[color:var(--brand-warning)] !text-[10px]">Tax &amp; NI estimates</span>
      </div>
      <div className="rounded-lg border border-[color:var(--brand-border)] overflow-hidden divide-y divide-[color:var(--brand-border)] text-[10px] sm:text-[11px]">
        <div className="grid grid-cols-[1.3fr_0.8fr_0.8fr_1fr_1fr] bg-[color:var(--brand-bg-2)] px-2 py-1.5 font-semibold text-[color:var(--brand-text-muted)]">
          <span>Staff</span><span>Hours</span><span>Overtime</span><span className="text-right">Gross</span><span className="text-right">Est. net</span>
        </div>
        {PAYROLL.map((row) => (
          <div key={row.name} className="grid grid-cols-[1.3fr_0.8fr_0.8fr_1fr_1fr] items-center bg-white px-2 py-1.5">
            <span className="font-semibold truncate">{row.name}</span>
            <span className="font-mono">{row.hours}</span>
            <span className="font-mono text-[color:var(--brand-text-muted)]">{row.overtime}</span>
            <span className="font-mono text-right">{row.gross}</span>
            <span className="font-mono text-right font-semibold text-[color:var(--brand-success)]">{row.net}</span>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between mt-2 text-[10px] text-[color:var(--brand-text-muted)]">
        <span>Hours pulled straight from attendance</span>
        <span className="text-[color:var(--brand-primary)] font-semibold">Finalise run →</span>
      </div>
    </ReplicaFrame>
  );
}

export function StaffReplica({ className = '' }) {
  return (
    <ReplicaFrame label="Employees" className={className}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5 text-xs font-semibold"><Users className="h-3.5 w-3.5 text-[color:var(--brand-primary)]" /> Team · 5 active</div>
        <span className="pill bg-[color:var(--brand-primary-soft)] text-[color:var(--brand-primary)] !text-[10px] inline-flex items-center gap-1"><Upload className="h-2.5 w-2.5" /> Import CSV / XLSX</span>
      </div>
      <div className="space-y-1.5">
        {STAFF.map((emp) => (
          <div key={emp.name} className="flex items-center gap-2 rounded-lg border border-[color:var(--brand-border)] bg-white px-2 py-1.5 text-[11px]">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[color:var(--brand-bg-2)] font-semibold text-[9px]">{emp.initials}</span>
            <span className="font-semibold flex-1 truncate">{emp.name}</span>
            <span className="text-[color:var(--brand-text-muted)] truncate">{emp.role}</span>
            <span className="font-mono text-[10px]">{emp.rate}</span>
            <span className="h-1.5 w-1.5 rounded-full bg-[color:var(--brand-success)]" aria-hidden="true" />
          </div>
        ))}
      </div>
    </ReplicaFrame>
  );
}

const RESOURCES = [
  { icon: ShieldCheck, name: 'Health & safety procedures', tag: 'H&S' },
  { icon: Flame, name: 'Fire evacuation plan', tag: 'Emergency' },
  { icon: FileText, name: 'Allergen matrix — Spring menu', tag: 'Kitchen' },
  { icon: CheckCircle2, name: 'New starter checklist', tag: 'Onboarding' },
];

export function ResourcesReplica({ className = '' }) {
  return (
    <ReplicaFrame label="Resources" className={className}>
      <div className="text-xs font-semibold mb-2">Staff resources</div>
      <div className="space-y-1.5">
        {RESOURCES.map((doc) => (
          <div key={doc.name} className="flex items-center gap-2 rounded-lg border border-[color:var(--brand-border)] bg-white px-2 py-2 text-[11px]">
            <doc.icon className="h-3.5 w-3.5 shrink-0 text-[color:var(--brand-primary)]" />
            <span className="font-semibold flex-1 truncate">{doc.name}</span>
            <span className="pill bg-[color:var(--brand-bg-2)] text-[color:var(--brand-text-muted)] !text-[9px]">{doc.tag}</span>
          </div>
        ))}
      </div>
      <div className="mt-2 text-[10px] text-[color:var(--brand-text-muted)]">Visible to your whole team in the employee portal.</div>
    </ReplicaFrame>
  );
}
